import React, {createContext, useCallback, useEffect, useRef, useState} from "react";
import apiFetch from "./api";
import { DEFAULT_VENUE_TYPE } from "../lib/consts/venueTypes";
import { isCognitoEnabled, cognitoRefreshSession, cognitoSignOut } from "../lib/cognito";

export const UserContext = createContext();

export const UserProvider = (props) => {
    const [token, setToken] = useState(localStorage.getItem("kongToken"));
    const cognitoRefreshTimer = useRef(null);
    const [ownedShops, setOwnedShops] = useState([]);
    const [allVenues, setAllVenues] = useState([]); // All venues for superusers
    const [email, setEmail] = useState("");
    const [accessPrivileges, setAccessPrivileges] = useState("");
    const [canManageProducts, setCanManageProducts] = useState(false);
    const [venueID, setVenueID] = useState(localStorage.getItem("selectedVenueID") || "");
    const [venueName, setVenueName] = useState(localStorage.getItem("selectedVenueName") || "");
    const [venueImage, setVenueImage] = useState(localStorage.getItem("selectedVenueImage") || "");
    const [venueType, setVenueType] = useState(localStorage.getItem("selectedVenueType") || DEFAULT_VENUE_TYPE);
    const [venueActivationStatus, setVenueActivationStatus] = useState(localStorage.getItem("selectedVenueActivationStatus") || "pending");
    const [venueSubscriptionStatus, setVenueSubscriptionStatus] = useState(localStorage.getItem("selectedVenueSubscriptionStatus") || "");
    // SCRUM-54: el guard distingue "prueba vencida" de "nunca activada" con esta fecha.
    const [venueTrialEndsAt, setVenueTrialEndsAt] = useState(localStorage.getItem("selectedVenueTrialEndsAt") || "");

    const persistSelectedVenue = useCallback((venue) => {
      const nextVenueID = String(venue?.id || "");
      const nextVenueName = venue?.name || "";
      const nextVenueImage = venue?.image_path || "";
      const nextVenueType = venue?.venue_type || DEFAULT_VENUE_TYPE;
      const nextVenueActivationStatus = venue?.activation_status || "pending";
      const nextVenueSubscriptionStatus = venue?.subscription_status || "";
      const nextVenueTrialEndsAt = venue?.trial_ends_at || "";

      setVenueID(nextVenueID);
      setVenueName(nextVenueName);
      setVenueImage(nextVenueImage);
      setVenueType(nextVenueType);
      setVenueActivationStatus(nextVenueActivationStatus);
      setVenueSubscriptionStatus(nextVenueSubscriptionStatus);
      setVenueTrialEndsAt(nextVenueTrialEndsAt);

      localStorage.setItem("selectedVenueID", nextVenueID);
      localStorage.setItem("selectedVenueName", nextVenueName);
      localStorage.setItem("selectedVenueImage", nextVenueImage);
      localStorage.setItem("selectedVenueType", nextVenueType);
      localStorage.setItem("selectedVenueActivationStatus", nextVenueActivationStatus);
      localStorage.setItem("selectedVenueSubscriptionStatus", nextVenueSubscriptionStatus);
      localStorage.setItem("selectedVenueTrialEndsAt", nextVenueTrialEndsAt);
    }, []);

    // Cognito token refresh — silently renews the ID token every 45 min.
    // Does nothing when AUTH_PROVIDER != cognito or when there is no token.
    useEffect(() => {
      if (!isCognitoEnabled || !token) return;

      const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

      const refresh = async () => {
        const refreshToken = localStorage.getItem("kongRefreshToken");
        const email = localStorage.getItem("kongEmail");
        if (!refreshToken || !email) return;

        try {
          const { idToken, refreshToken: newRefresh } = await cognitoRefreshSession(email, refreshToken);
          localStorage.setItem("kongToken", idToken);
          localStorage.setItem("kongRefreshToken", newRefresh);
          setToken(idToken);
          console.log("[UserContext] Cognito session refreshed silently.");
        } catch (err) {
          console.warn("[UserContext] Cognito refresh failed — forcing re-login:", err.message);
          cognitoSignOut();
          setToken(null);
          localStorage.removeItem("kongToken");
          localStorage.removeItem("kongRefreshToken");
          localStorage.removeItem("kongEmail");
        }
      };

      // Clear any existing timer before setting a new one
      if (cognitoRefreshTimer.current) clearInterval(cognitoRefreshTimer.current);
      cognitoRefreshTimer.current = setInterval(refresh, REFRESH_INTERVAL_MS);

      return () => {
        if (cognitoRefreshTimer.current) clearInterval(cognitoRefreshTimer.current);
      };
    }, [token]);

    useEffect(() => {
        const fetchWithRetry = async (url, options, retries = 3) => {
          for (let i = 0; i < retries; i++) {
            try {
              const res = await apiFetch(url, options);
              if (res.status === 502 || res.status === 503 || res.status === 504) {
                if (i < retries - 1) {
                  await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                  continue;
                }
              }
              return res;
            } catch (err) {
              if (i < retries - 1) {
                await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                continue;
              }
              throw err;
            }
          }
        };

        const fetchUser = async () => {
          const requestOptions = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          };
      
          try {
            if (!token) {
              return;
            }
            
            const response = await fetchWithRetry(`/v1/users/me`, requestOptions);
            
            if (!response.ok) {
              if (response.status === 401 || response.status === 403) {
                if (isCognitoEnabled) cognitoSignOut();
                setToken(null);
                localStorage.removeItem("kongToken");
                localStorage.removeItem("kongRefreshToken");
                localStorage.removeItem("kongEmail");
                return;
              }
              // Never clear session on gateway/network errors (502, 503, 504) — backend may be cold after idle
              if (response.status === 502 || response.status === 503 || response.status === 504) {
                console.warn(`[UserContext] /v1/users/me returned ${response.status}, keeping session (retry later)`);
                return;
              }
              console.warn(`[UserContext] /v1/users/me returned ${response.status}, keeping session`);
              return;
            }
            
            const data = await response.json();
            
            console.log('[UserContext] User data received:', {
              email: data?.email,
              access_privileges: data?.access_privileges,
              venue_id: data?.venue_id,
              venue: data?.venue,
              owned_venues_count: data?.owned_venues?.length || 0,
              owned_venues: data?.owned_venues
            });

            setOwnedShops(data?.owned_venues || []); 
            setEmail(data?.email || "");
            setAccessPrivileges(data?.access_privileges || "");
            setCanManageProducts(!!data?.can_manage_products);

            // If superuser, fetch all venues
            if (data?.access_privileges === 'superadmin') {
              try {
                const venuesResponse = await apiFetch('/v1/venues/all', requestOptions);
                if (venuesResponse.ok) {
                  const venuesData = await venuesResponse.json();
                  setAllVenues(venuesData || []);
                  console.log('[UserContext] Loaded all venues for superuser:', venuesData.length);
                  
                  // If no venue is selected, try to use the one from localStorage or first venue
                  const savedVenueID = localStorage.getItem("selectedVenueID");
                  if (savedVenueID && venuesData.find(v => String(v.id) === savedVenueID)) {
                    const savedVenue = venuesData.find(v => String(v.id) === savedVenueID);
                    persistSelectedVenue(savedVenue);
                  } else if (venuesData.length > 0) {
                    persistSelectedVenue(venuesData[0]);
                  }
                }
              } catch (err) {
                console.error('[UserContext] Error fetching all venues:', err);
              }
            } else if (data?.access_privileges === 'staff') {
              // For staff users, use venue_id from the response
              console.log('[UserContext] Processing staff user, venue data:', {
                venue_id: data?.venue_id,
                venue: data?.venue,
                hasVenue: !!data?.venue
              });
              
              if (data?.venue_id) {
                const staffVenueID = String(data.venue_id);
                // For STAFF, ALWAYS use venue data from response (never use cached data)
                // This ensures STAFF always sees the correct venue they are assigned to
                const venueName = data?.venue?.name || "";
                const venueImage = data?.venue?.image_path || "";
                const nextVenueType = data?.venue?.venue_type || DEFAULT_VENUE_TYPE;
                
                // Clear any incorrect cached venue data for STAFF
                if (localStorage.getItem("selectedVenueID") !== staffVenueID) {
                  console.log('[UserContext] Clearing incorrect cached venue data for STAFF');
                  localStorage.removeItem("selectedVenueID");
                  localStorage.removeItem("selectedVenueName");
                  localStorage.removeItem("selectedVenueImage");
                }
                
                // Force update venue state - this will trigger re-renders in components
                setVenueID(staffVenueID);
                setVenueName(venueName);
                setVenueImage(venueImage);
                setVenueType(nextVenueType);
                setVenueActivationStatus(data?.venue?.activation_status || "pending");
                setVenueSubscriptionStatus(data?.venue?.subscription_status || "");
                setVenueTrialEndsAt(data?.venue?.trial_ends_at || "");
                localStorage.setItem("selectedVenueID", staffVenueID);
                localStorage.setItem("selectedVenueName", venueName);
                localStorage.setItem("selectedVenueImage", venueImage);
                localStorage.setItem("selectedVenueType", nextVenueType);
                localStorage.setItem("selectedVenueActivationStatus", data?.venue?.activation_status || "pending");
                localStorage.setItem("selectedVenueSubscriptionStatus", data?.venue?.subscription_status || "");
                localStorage.setItem("selectedVenueTrialEndsAt", data?.venue?.trial_ends_at || "");
                console.log('[UserContext] ✅ Set venue data for staff:', {
                  id: staffVenueID,
                  name: venueName,
                  image: venueImage,
                  venueID_type: typeof staffVenueID,
                  fromResponse: !!data?.venue,
                  venueIdFromResponse: data?.venue?.id,
                  fullVenueData: data?.venue,
                  venue_id_from_data: data?.venue_id
                });
                
                // Warn if venue name is missing
                if (!venueName) {
                  console.warn('[UserContext] STAFF venue name is missing from response!', {
                    venue_id: staffVenueID,
                    venue_data: data?.venue
                  });
                }
              } else {
                console.error('[UserContext] ❌ Staff user has no venue_id assigned in response!', data);
                // Para staff sin venue_id, mostrar mensaje claro pero no romper la aplicación
                setVenueID("");
                setVenueName("⚠️ Sin tienda asignada");
                setVenueImage("");
                setVenueType(DEFAULT_VENUE_TYPE);
                setVenueActivationStatus("pending");
                setVenueSubscriptionStatus("");
                setVenueTrialEndsAt("");
                // Clear cached data if no venue_id
                localStorage.removeItem("selectedVenueID");
                localStorage.removeItem("selectedVenueName");
                localStorage.removeItem("selectedVenueImage");
                localStorage.removeItem("selectedVenueType");
                localStorage.removeItem("selectedVenueActivationStatus");
                localStorage.removeItem("selectedVenueSubscriptionStatus");
                localStorage.removeItem("selectedVenueTrialEndsAt");
              }
            } else {
              // For non-superusers (owners), use owned_venues
              const ownedVenues = data?.owned_venues || [];
              
              // Try to use saved venue if it exists and is in owned_venues
              const savedVenueID = localStorage.getItem("selectedVenueID");
              if (savedVenueID && ownedVenues.find(v => String(v.id) === savedVenueID)) {
                const savedVenue = ownedVenues.find(v => String(v.id) === savedVenueID);
                console.log('[UserContext] Using saved venue for owner:', {
                  id: savedVenue.id,
                  name: savedVenue.name
                });
                persistSelectedVenue(savedVenue);
              } else if (ownedVenues.length > 0) {
                const firstOwnedVenue = ownedVenues[0];
                console.log('[UserContext] Setting first owned venue:', {
                  id: firstOwnedVenue.id,
                  name: firstOwnedVenue.name
                });
                persistSelectedVenue(firstOwnedVenue);
              } else {
                console.warn('[UserContext] No owned venues found for user');
                setVenueID("");
                setVenueName("");
                setVenueImage("");
                setVenueType(DEFAULT_VENUE_TYPE);
                setVenueActivationStatus("pending");
                setVenueSubscriptionStatus("");
                setVenueTrialEndsAt("");
                localStorage.removeItem("selectedVenueID");
                localStorage.removeItem("selectedVenueName");
                localStorage.removeItem("selectedVenueImage");
                localStorage.removeItem("selectedVenueType");
                localStorage.removeItem("selectedVenueActivationStatus");
                localStorage.removeItem("selectedVenueSubscriptionStatus");
                localStorage.removeItem("selectedVenueTrialEndsAt");
              }
            }
            
            // Solo guardar token si la respuesta fue exitosa
            localStorage.setItem("kongToken", token);
          } catch (error) {
            console.error("Error fetching user data:", error);
            // Only clear token on explicit auth errors (401/403), not on network/gateway errors (502, etc.)
            const msg = (error?.message || "").toLowerCase();
            if (msg.includes("401") || msg.includes("403")) {
              if (isCognitoEnabled) cognitoSignOut();
              setToken(null);
              localStorage.removeItem("kongToken");
              localStorage.removeItem("kongRefreshToken");
              localStorage.removeItem("kongEmail");
            }
          }
        };
        fetchUser();
      }, [token, persistSelectedVenue]);

       // ownedShops state updated
            

    // Function to change selected venue (for superusers)
    const changeVenue = useCallback((
      venueId,
      venueName,
      venueImagePath = "",
      nextVenueType = DEFAULT_VENUE_TYPE,
      nextVenueActivationStatus = "pending",
      nextVenueSubscriptionStatus = "",
      nextVenueTrialEndsAt = ""
    ) => {
      setVenueID(String(venueId));
      setVenueName(venueName || "");
      setVenueImage(venueImagePath || "");
      setVenueType(nextVenueType || DEFAULT_VENUE_TYPE);
      setVenueActivationStatus(nextVenueActivationStatus || "pending");
      setVenueSubscriptionStatus(nextVenueSubscriptionStatus || "");
      setVenueTrialEndsAt(nextVenueTrialEndsAt || "");
      localStorage.setItem("selectedVenueID", String(venueId));
      localStorage.setItem("selectedVenueName", venueName || "");
      localStorage.setItem("selectedVenueImage", venueImagePath || "");
      localStorage.setItem("selectedVenueType", nextVenueType || DEFAULT_VENUE_TYPE);
      localStorage.setItem("selectedVenueActivationStatus", nextVenueActivationStatus || "pending");
      localStorage.setItem("selectedVenueSubscriptionStatus", nextVenueSubscriptionStatus || "");
      localStorage.setItem("selectedVenueTrialEndsAt", nextVenueTrialEndsAt || "");
      console.log('[UserContext] Venue changed to:', {
        id: venueId,
        name: venueName,
        image: venueImagePath,
        venueType: nextVenueType,
        activationStatus: nextVenueActivationStatus,
        subscriptionStatus: nextVenueSubscriptionStatus,
      });
    }, []);

    // Function to refresh venues data
    const refreshVenues = useCallback(async () => {
      if (!token) return;
      
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      };

      try {
        // Refresh user data to get updated owned_venues
        const response = await fetch(`/v1/users/me`, requestOptions);
        if (!response.ok) return;
        
        const data = await response.json();
        setOwnedShops(data?.owned_venues || []);
        
        // If superuser, refresh all venues
        if (data?.access_privileges === 'superadmin') {
          const venuesResponse = await fetch('/v1/venues/all', requestOptions);
          if (venuesResponse.ok) {
            const venuesData = await venuesResponse.json();
            setAllVenues(venuesData || []);
            
            // Update selected venue if it exists in the refreshed list
            const currentVenueID = venueID;
            if (currentVenueID) {
              const updatedVenue = venuesData.find(v => String(v.id) === currentVenueID);
              if (updatedVenue) {
                persistSelectedVenue(updatedVenue);
              }
            }
          }
        } else {
          // For owners, update selected venue from owned_venues
          const ownedVenues = data?.owned_venues || [];
          const currentVenueID = venueID;
          if (currentVenueID) {
            const updatedVenue = ownedVenues.find(v => String(v.id) === currentVenueID);
            if (updatedVenue) {
              persistSelectedVenue(updatedVenue);
            }
          }
        }
      } catch (error) {
        console.error('[UserContext] Error refreshing venues:', error);
      }
    }, [token, venueID, persistSelectedVenue]);

    return(
        <UserContext.Provider value={[
          token, 
          setToken, 
          ownedShops, 
          venueID, 
          venueName, 
          venueImage,
          email, 
          accessPrivileges,
          allVenues,
          changeVenue,
          refreshVenues,
          venueType,
          venueActivationStatus,
          venueSubscriptionStatus,
          canManageProducts,
          venueTrialEndsAt
        ]}>
            {props.children}
        </UserContext.Provider>
    )

};
